import React from 'react';
import {
  Globe,
  Palette,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Type,
  Building2,
} from 'lucide-react';
import { useSite } from '../../../context/SiteContext';
import { ColorPickerField } from './common/ColorPickerField';

export const GlobalSettingsInspector: React.FC = () => {
  const { activeSite, updateSiteData } = useSite();

  if (!activeSite) return null;

  const settings = activeSite.settings || {};

  const handleUpdateSettings = (updates: Partial<typeof settings>) => {
    updateSiteData({
      settings: {
        ...settings,
        ...updates,
      },
    });
  };

  return (
    <div className="p-4 space-y-6">
      {/* Overview Banner */}
      <div className="p-4 rounded-2xl bg-linear-to-br from-blue-900/30 to-indigo-900/20 border border-blue-500/30 space-y-2">
        <div className="flex items-center gap-2 text-blue-400">
          <Globe className="w-4 h-4" />
          <h3 className="text-xs font-black tracking-wide uppercase">Site Genel Ayarları</h3>
        </div>
        <p className="text-[11px] text-slate-300 leading-relaxed">
          Bir bölüm seçmediğinizde site geneli marka bilgileri, global renk teması ve iletişim detayları burada listelenir.
        </p>
      </div>

      {/* Brand & Industry */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Marka ve Sektör</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Site / Marka Adı</label>
          <input
            type="text"
            value={settings.businessName || activeSite.title || activeSite.name || ''}
            onChange={(e) => {
              handleUpdateSettings({ businessName: e.target.value });
              updateSiteData({ name: e.target.value, title: e.target.value });
            }}
            placeholder="Örn: Mavi Tur Yatçılık"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Slogan / Tagline</label>
          <input
            type="text"
            value={settings.tagline || ''}
            onChange={(e) => handleUpdateSettings({ tagline: e.target.value })}
            placeholder="Örn: Ege ve Akdeniz'de Unutulmaz Rotalar"
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Theme Colors */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Global Renk Paleti</h4>
        </div>

        <ColorPickerField
          label="Ana Marka Rengi (Primary)"
          value={settings.primaryColor}
          defaultValue="#0F172A"
          onChange={(color) => handleUpdateSettings({ primaryColor: color })}
        />

        <ColorPickerField
          label="Vurgu Rengi (Accent)"
          value={settings.accentColor}
          defaultValue="#2563EB"
          onChange={(color) => handleUpdateSettings({ accentColor: color })}
        />

        <ColorPickerField
          label="İkincil Renk (Secondary)"
          value={settings.secondaryColor}
          defaultValue="#64748B"
          onChange={(color) => handleUpdateSettings({ secondaryColor: color })}
        />

        <ColorPickerField
          label="Site Zemin Rengi (Background)"
          value={settings.backgroundColor}
          defaultValue="#FFFFFF"
          onChange={(color) => handleUpdateSettings({ backgroundColor: color })}
        />
      </div>

      {/* Global Contact Info */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 text-cyan-400" />
          <h4 className="text-xs font-bold text-slate-200">Global İletişim Bilgileri</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Phone className="w-3 h-3 text-cyan-400" />
            <span>Telefon Numarası</span>
          </label>
          <input
            type="text"
            value={settings.phone || ''}
            onChange={(e) => handleUpdateSettings({ phone: e.target.value })}
            placeholder="+90 (212) 800 50 00"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <MessageCircle className="w-3 h-3 text-emerald-400" />
            <span>WhatsApp Numarası</span>
          </label>
          <input
            type="text"
            value={settings.socialLinks?.whatsapp || settings.phone || ''}
            onChange={(e) =>
              handleUpdateSettings({
                socialLinks: {
                  ...(settings.socialLinks || {}),
                  whatsapp: e.target.value,
                },
              })
            }
            placeholder="+90 532 000 00 00"
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Mail className="w-3 h-3 text-blue-400" />
            <span>E-posta Adresi</span>
          </label>
          <input
            type="email"
            value={settings.email || ''}
            onChange={(e) => handleUpdateSettings({ email: e.target.value })}
            placeholder="info@sirketiniz.com"
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-rose-400" />
            <span>Adres & Konum</span>
          </label>
          <textarea
            value={settings.address || ''}
            onChange={(e) => handleUpdateSettings({ address: e.target.value })}
            rows={2}
            placeholder="Şirket merkezi adresi..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Global Font Family */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Type className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Global Yazı Tipi (Font)</h4>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'Plus Jakarta Sans', label: 'Plus Jakarta', font: 'sans-serif' },
            { id: 'Inter', label: 'Inter', font: 'sans-serif' },
            { id: 'Outfit', label: 'Outfit Modern', font: 'sans-serif' },
            { id: 'Playfair Display', label: 'Playfair Serif', font: 'serif' },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => handleUpdateSettings({ fontBody: f.id, fontHeading: f.id, fontFamily: f.id })}
              className={`p-2.5 rounded-xl border text-left transition ${
                (settings.fontBody || settings.fontFamily || 'Plus Jakarta Sans') === f.id
                  ? 'bg-blue-600/20 border-blue-500 text-white ring-1 ring-blue-500/30'
                  : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              <div className="text-xs font-bold">{f.label}</div>
              <div className="text-[10px] text-slate-400 font-serif">Aa Bb Cc 123</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
